import { graphHooks } from '@tunasong/graph-lib/react'
import type { GroupedPoll, Poll, PollGroup, PollOption, PollResponse } from '@tunasong/schemas'
import { Button, ConfirmDialog, Select, TextField, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import {
  addGroup,
  addOptionToGroup,
  getEffectiveQuota,
  getGroupOptionIndices,
  getGroupVoters,
  getLiveGroups,
  moveOptionToGroup,
  moveOptionToNewGroup,
  newGroup,
  removeGroup,
  updateGroup,
} from './groups.js'
import { NewGroupDialog } from './new-group-dialog.js'
import { getOptionLabel } from './option-label.js'
import type { OptionRowHandlers } from './option-row.js'
import { OptionRow } from './option-row.js'
import css from './properties.module.css'

export interface GroupListEditorProps {
  element: GroupedPoll
  responses: PollResponse[]
  answerCounts: Map<number, number>
  /** The option-row handlers owned by Properties, so renaming and removal work the same as in a plain poll. */
  rowHandlers: OptionRowHandlers
  /**
   * Write from the latest poll at event time, never from this render's `element`: a write landing before the
   * re-render would otherwise overwrite the one before it (GH #1966).
   */
  update(write: (poll: GroupedPoll) => Poll): void
}

/** The Group select's value for "New group…": not a group id, so it can never collide with one. */
const newGroupValue = '__new-group__'

interface GroupedOptionRowProps {
  idx: number
  option: PollOption
  groupId: string
  /** Every live group. "New group…" follows them, so the select shows even when there is only one. */
  groups: PollGroup[]
  answerCount: number
  rowHandlers: OptionRowHandlers
  onMove(idx: number, groupId: string, label: string): void
}

/**
 * An option row with a Group select, so an option can be moved without deleting and re-adding it. "New group…" sits
 * in the same select, next to where the owner already is, instead of at the bottom of a long list.
 */
const GroupedOptionRow: FC<GroupedOptionRowProps> = props => {
  const { idx, option, groupId, groups, answerCount, rowHandlers, onMove } = props
  const { entity } = graphHooks.useEntity(option.entityRef)

  return (
    <div className={css.groupedOption}>
      <OptionRow idx={idx} option={option} answerCount={answerCount} handlers={rowHandlers} />
      {/* No visible label: it would repeat on every row, and the label above the select pushed it off-centre */}
      <Select
        aria-label="Group"
        className={css.compactSelect}
        value={groupId}
        onChange={ev => onMove(idx, ev.target.value, getOptionLabel(option, entity?.name))}
      >
        {groups.map(group => (
          <option key={group.id} value={group.id}>
            {group.name}
          </option>
        ))}
        <hr />
        <option value={newGroupValue}>New group…</option>
      </Select>
    </div>
  )
}

/**
 * Groups of options, each with a name and its own picks-per-voter. Group names can always change — answers point
 * at options, not names. Deleting a group tombstones it and its options; with answers in it, the owner confirms.
 */
export const GroupListEditor: FC<GroupListEditorProps> = props => {
  const { element, responses, answerCounts, rowHandlers, update } = props
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string; voters: number } | null>(null)
  const [pendingMove, setPendingMove] = useState<{ idx: number; groupId: string; text: string } | null>(null)
  /** "New group…" asks for the name first; `text` warns that the option's picks move with it. */
  const [pendingNewGroup, setPendingNewGroup] = useState<{ idx: number; text?: string } | null>(null)

  const groups = getLiveGroups(element)

  const handleDelete = (groupId: string, name: string) => {
    const voters = getGroupVoters(element, responses, groupId).length
    if (voters > 0) {
      setPendingDelete({ id: groupId, name, voters })
      return
    }
    update(poll => removeGroup(poll, groupId))
  }

  const handleConfirmDelete = () => {
    if (pendingDelete) {
      const { id } = pendingDelete
      update(poll => removeGroup(poll, id))
    }
    setPendingDelete(null)
  }

  /**
   * Answers point at the option, so they move with it. Moving an option people picked changes which question their
   * pick answers, so the owner confirms; an unpicked option moves at once. A new group is named first, and the
   * group is only created, in the same write that moves the option, once the owner confirms the name.
   */
  const handleMove = (idx: number, groupId: string, label: string) => {
    const pickers = responses.filter(response => response.choices?.[idx]).length
    const who = pickers === 1 ? '1 person' : `${pickers} people`
    if (groupId === newGroupValue) {
      setPendingNewGroup({
        idx,
        text: pickers > 0 ? `${who} picked ${label}. Their picks move with it to the new group.` : undefined,
      })
      return
    }
    if (pickers === 0) {
      update(poll => moveOptionToGroup(poll, idx, groupId))
      return
    }
    const target = groups.find(group => group.id === groupId)?.name ?? ''
    setPendingMove({ idx, groupId, text: `${who} picked ${label}. Their picks move with it to ${target}.` })
  }

  const handleConfirmMove = () => {
    if (pendingMove) {
      const { idx, groupId } = pendingMove
      update(poll => moveOptionToGroup(poll, idx, groupId))
    }
    setPendingMove(null)
  }

  const handleCreateGroup = (name: string) => {
    if (pendingNewGroup) {
      const { idx } = pendingNewGroup
      update(poll => moveOptionToNewGroup(poll, idx, newGroup(name)))
    }
    setPendingNewGroup(null)
  }

  const deleteText = pendingDelete
    ? `${pendingDelete.voters === 1 ? '1 person has' : `${pendingDelete.voters} people have`} answered in ${
        pendingDelete.name
      }. Their answers for this group will be discarded.`
    : ''

  return (
    <>
      {groups.map(group => {
        const indices = getGroupOptionIndices(element, group.id)
        return (
          <section key={group.id} aria-label={`Group ${group.name}`} className={css.group}>
            <div className={css.groupFields}>
              <TextField
                label="Group name"
                aria-label="Group name"
                defaultValue={group.name}
                variant="standard"
                onChange={ev => {
                  const name = ev.target.value
                  update(poll => updateGroup(poll, group.id, { name }))
                }}
              />
              <Select
                label="Picks per voter"
                className={css.compactSelect}
                value={getEffectiveQuota(element, group)}
                onChange={ev => {
                  const quota = Number(ev.target.value)
                  update(poll => updateGroup(poll, group.id, { quota }))
                }}
              >
                {Array.from({ length: Math.max(1, indices.length) }, (_, i) => i + 1).map(count => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </Select>
              <Button onClick={() => handleDelete(group.id, group.name)}>Delete group</Button>
            </div>
            {indices.length === 0 ? (
              <Typography variant="caption" color="textSecondary">
                No options in this group yet.
              </Typography>
            ) : null}
            {indices.map(idx => (
              <GroupedOptionRow
                key={idx}
                idx={idx}
                option={element.pollOptions[idx]}
                groupId={group.id}
                groups={groups}
                answerCount={answerCounts.get(idx) ?? 0}
                rowHandlers={rowHandlers}
                onMove={handleMove}
              />
            ))}
            <Button onClick={() => update(poll => addOptionToGroup(poll, group.id))} className={css.createButton}>
              Create option
            </Button>
          </section>
        )
      })}
      <Button
        onClick={() => update(poll => addGroup(poll, newGroup(`Group ${poll.pollGroups.length + 1}`)))}
        className={css.createButton}
      >
        Add group
      </Button>
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete this group?"
        text={deleteText}
        okText="Delete group"
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
      />
      <ConfirmDialog
        open={pendingMove !== null}
        title="Move this option?"
        text={pendingMove?.text ?? ''}
        okText="Move option"
        onCancel={() => setPendingMove(null)}
        onConfirm={handleConfirmMove}
      />
      {pendingNewGroup ? (
        <NewGroupDialog
          defaultName={`Group ${element.pollGroups.length + 1}`}
          text={pendingNewGroup.text}
          onCancel={() => setPendingNewGroup(null)}
          onCreate={handleCreateGroup}
        />
      ) : null}
    </>
  )
}

export default GroupListEditor
